import React from "react";
import { motion } from "framer-motion";

const aiAgents = [
  {
    name: "AI Receptionist (24/7 inbound)",
    example: "e.g. Dentina.ai, Arini",
    price: "$300 – $500",
    type: "fixed",
  },
  {
    name: "AI Schedule Optimizer",
    example: "e.g. PatientDesk",
    price: "$1,000",
    type: "fixed",
  },
  {
    name: "AI Hygiene Recall",
    example: "e.g. PatientDesk, Recall Solutions",
    price: "$1,000",
    type: "fixed",
  },
  {
    name: "AI Case Acceptance",
    example: "e.g. Codent, SmileSnap",
    price: "$500",
    type: "fixed",
  },
  {
    name: "AI Scribe (clinical notes)",
    example: "e.g. Toothfairy AI, DentalScribe",
    price: "$300",
    type: "fixed",
  },
  {
    name: "Voice Perio",
    example: "e.g. Bola AI · 2 providers @ $100",
    price: "$200",
    type: "fixed",
  },
];

// Asha-only AI capabilities with no direct vendor comparison.
// Numbers are conservative estimates of the staff time it would take a
// front-desk / coordinator to do the work manually, at ~$20/hr.
const ashaOnlyClinical = [
  {
    name: "AI Patient Intake",
    example: "No vendor — staff time to enter intake / scan IDs / insurance",
    price: "~$400",
    type: "fixed",
    staffEst: true,
  },
  {
    name: "AI Treatment Planner",
    example: "No vendor — coordinator time to draft treatment plans",
    price: "~$500",
    type: "fixed",
    staffEst: true,
  },
];

const revenueCycle = [
  {
    name: "Insurance Verification Calls",
    example: "e.g. eAssist, Wisdom, DentalRev",
    price: "$2,400",
    type: "fixed",
  },
  {
    name: "Insurance Billing",
    example: "e.g. eAssist, Wisdom, DCS — bundled submission, scrubbing, EOB posting",
    price: "3.5%",
    priceUnit: "of collections",
    type: "percent",
    pct: 0.035,
  },
  {
    // Lassie is intentionally dual-listed for visibility, but EOB posting
    // is already bundled into the 3.5% Insurance Billing line above —
    // so this row is shown as a reference price and excluded from the total.
    name: "AI EOB Posting",
    example: "e.g. Lassie · already bundled into Insurance Billing above",
    price: "2%",
    priceUnit: "of collections",
    type: "percent",
    pct: 0.02,
    excludeFromTotal: true,
  },
  {
    name: "Patient Billing & Collections",
    example: "e.g. eAssist, Wisdom, Pearly",
    price: "$1,000 – $1,200",
    type: "fixed",
  },
  {
    name: "Payment Reconciliation",
    example: "e.g. eAssist, Wisdom",
    price: "$200",
    type: "fixed",
  },
];

const ashaOnlyOps = [
  {
    name: "AI Practice Manager (Copilot)",
    example: "No vendor — manager time on KPI / reporting / ad-hoc analysis",
    price: "~$400",
    type: "fixed",
    staffEst: true,
  },
  {
    name: "AI Fax Manager",
    example: "No vendor — staff time triaging inbound faxes & referrals",
    price: "~$250",
    type: "fixed",
    staffEst: true,
  },
];

const columns = [
  {
    groups: [
      { label: "AI Agents — vendor comparison", items: aiAgents },
      { label: "Clinical AI — staff time estimate*", items: ashaOnlyClinical },
    ],
  },
  {
    groups: [
      { label: "Revenue Cycle Services", items: revenueCycle },
      { label: "Operations AI — staff time estimate*", items: ashaOnlyOps },
    ],
  },
];

const allItems = [...aiAgents, ...ashaOnlyClinical, ...revenueCycle, ...ashaOnlyOps];

const parseRange = (price) => {
  const cleaned = price.replace(/\$|,/g, "");
  const nums = cleaned.split("–").map((s) => parseInt(s.trim(), 10));
  return nums.length === 2 ? nums : [nums[0], nums[0]];
};

// Totals split by type: fixed monthly $ and a separate % of collections.
// We don't collapse the % rows into dollars — we report them as the actual
// percentage so the total reads as "$X /mo + Y% of collections".
// "~$X" prefixes (used for staff-time estimates) are stripped before parsing.
const fixedTotals = allItems
  .filter((item) => item.type !== "percent")
  .reduce(
    (acc, item) => {
      const cleaned = item.price.replace(/~/g, "");
      const [lo, hi] = parseRange(cleaned);
      return { lo: acc.lo + lo, hi: acc.hi + hi };
    },
    { lo: 0, hi: 0 },
  );

const percentTotal = allItems
  .filter((item) => item.type === "percent" && !item.excludeFromTotal)
  .reduce((acc, item) => acc + item.pct, 0); // 3.5% (Lassie row is reference-only)

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;
const fmtPct = (p) => `${(p * 100).toFixed(p * 100 % 1 === 0 ? 0 : 1)}%`;

const PricingComparisonV3Slide = () => {
  return (
    <div className="h-full w-full bg-[#f8fafc] flex items-stretch p-5 xl:p-8 relative overflow-hidden font-sans">
      {/* Ambient gradient blurs — blue scheme */}
      <div className="absolute top-[-200px] left-[-100px] w-[600px] h-[600px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #bfdbfe 0%, #c4b5fd 50%, #93c5fd 100%)" }} />
      <div className="absolute bottom-[-200px] right-[-100px] w-[700px] h-[700px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)" }} />
      <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40 pointer-events-none" />

      <div className="w-full flex flex-col gap-6 relative z-10 h-full max-w-[1280px] mx-auto justify-center">
        {/* Top — title row */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-end justify-between gap-8 flex-shrink-0"
        >
          <div className="flex-1">
            <h2 className="text-[32px] lg:text-[40px] font-extrabold text-slate-900 tracking-tight leading-[1.05]">
              How much would it cost to{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                piecemeal the AI we offer?
              </span>
            </h2>
            <p className="text-[14.5px] text-slate-600 leading-snug font-medium mt-2 max-w-[820px]">
              Buying AI agents from point-vendors plus a revenue cycle service
              for billing, posting, and verification adds up fast — and you
              still get a stitched-together stack that doesn't share context.
            </p>
          </div>
        </motion.div>

        {/* Two-column line-item table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          className="rounded-[24px] overflow-hidden shadow-[0_20px_80px_-20px_rgba(0,0,0,0.12)] border border-slate-200 bg-white flex flex-col flex-1 min-h-0 relative"
        >
          {/* Two side-by-side columns; each column has multiple sub-groups */}
          <div className="flex-1 grid grid-cols-2 min-h-0">
            {columns.map((column, ci) => (
              <div
                key={ci}
                className={`flex flex-col min-h-0 ${ci > 0 ? "border-l border-slate-200" : ""}`}
              >
                {column.groups.map((group, gi) => (
                  <React.Fragment key={gi}>
                    {/* Group header */}
                    <div
                      className={`grid grid-cols-[1fr_140px] bg-slate-50/90 border-slate-200 flex-shrink-0 ${
                        gi === 0 ? "border-b" : "border-y"
                      }`}
                    >
                      <div className="px-5 py-2.5 flex items-center gap-2">
                        <div
                          className={`w-1 h-4 rounded-full ${
                            group.label.includes("staff time")
                              ? "bg-amber-400/70"
                              : "bg-blue-400/70"
                          }`}
                        />
                        <span className="text-[11px] font-black uppercase tracking-widest text-slate-700 leading-tight">
                          {group.label}
                        </span>
                      </div>
                      <div className="px-4 py-2.5 text-right border-l border-slate-200">
                        <span className="text-[10.5px] font-black uppercase tracking-widest text-slate-400">
                          {gi === 0 ? "Monthly cost" : ""}
                        </span>
                      </div>
                    </div>
                    {/* Rows */}
                    {group.items.map((item, i) => (
                      <motion.div
                        key={`${ci}-${gi}-${i}`}
                        initial={{ opacity: 0, x: 10 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 + i * 0.04, duration: 0.3 }}
                        className="grid grid-cols-[1fr_140px] border-b border-slate-100 hover:bg-slate-50/70 transition-colors flex-1 min-h-0"
                      >
                        <div className="px-5 py-2.5 flex flex-col justify-center">
                          <span className="text-[14px] font-semibold text-slate-900 leading-tight">
                            {item.name}
                          </span>
                          <span className="text-[11.5px] text-slate-400 italic mt-1 leading-tight">
                            {item.example}
                          </span>
                        </div>
                        <div className="px-4 py-2.5 flex flex-col items-end justify-center border-l border-slate-100">
                          <span className="text-[15px] font-bold text-slate-800 whitespace-nowrap leading-tight">
                            {item.price}
                            {item.type === "fixed" && (
                              <span className="text-[11px] font-semibold text-slate-400">
                                {" "}/mo
                              </span>
                            )}
                          </span>
                          {item.type === "percent" && (
                            <span className="text-[10.5px] text-slate-400 italic leading-tight">
                              {item.priceUnit}
                            </span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            ))}
          </div>

          {/* Footnote for staff time estimates */}
          <div className="px-5 py-2 bg-slate-50 border-t border-slate-100 flex-shrink-0">
            <span className="text-[10.5px] text-slate-500 italic leading-snug">
              * No direct vendor for these capabilities — figure shown is a
              conservative estimate of the staff cost to do the work manually
              at ~$20/hr.
            </span>
          </div>

          {/* Total bar (full width) */}
          <div className="grid grid-cols-[1fr_320px] border-t-2 border-slate-200 bg-slate-50 flex-shrink-0 z-20 relative shadow-[0_-10px_20px_rgba(0,0,0,0.03)]">
            <div className="px-6 py-3.5 flex flex-col justify-center">
              <span className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
                Total — piecemeal AI + revenue cycle
              </span>
              <span className="text-[11px] text-slate-400 italic mt-0.5">
                Fixed monthly fees plus a percentage of your collections
              </span>
            </div>
            <div className="px-5 py-3.5 flex items-center justify-end border-l border-slate-200">
              <span className="text-[19px] font-black text-slate-900 whitespace-nowrap">
                {fmt(fixedTotals.lo)}–{fmt(fixedTotals.hi)}
                <span className="text-[11px] font-semibold text-slate-400">
                  {" "}/mo
                </span>
                <span className="text-slate-400 mx-1.5 font-light">+</span>
                {fmtPct(percentTotal)}
                <span className="text-[11px] font-semibold text-slate-400">
                  {" "}of collections
                </span>
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingComparisonV3Slide;
